import moment from "moment";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getRequest, postRequest, showMessage } from '../../../../config.js';
import { setCompany } from "../../../../reducers/companyReducer.js";
import { toggleModal } from "../../../../reducers/modalReducer.js";
import AuditLogIcon from "../../../../../images/dataRepo/auditLog.svg"
import TeamPermissionLogs from "../../../../components/modals/teamPermissionLogs/teamPermissionLogs.jsx";

export default function TeamPermissions({ company, setEditUser, setUserAction }) {

	const dispatch = useDispatch();
	let [groupedSections, setGroupedSections] = useState(null)
	const companyId = company?.['_id'] || (typeof window !== 'undefined' ? window.location.pathname.split("/").slice(-1)[0] : '');
	const user = useSelector((state) => state.user);
	const [allLogs, setAllLogs] = useState([]);
	const [tempModules, setTempModules] = useState([]);
	const [openAccordions, setOpenAccordions] = useState({});
	const [savingMemberId, setSavingMemberId] = useState(null);
	const isAdmin = user?.['_id'] && company?.['creatorId']?.['_id'] && user['_id'] === company['creatorId']['_id'];
	const teamMembers = (company['members'] || []).filter((mem) => mem._id !== company?.['creatorId']?.['_id']);

	function capitalizeFirstLetter(val) {
		return String(val).charAt(0).toUpperCase() + String(val).slice(1);
	}

	const getViewableURL = (url) => {
		window.open(url, "_blank");
	};

	useEffect(() => {
		getRequest('/modules/all-modules-section', 'not-showMessage').then(res => {
			if (res?.code === 200) {
				const body = res.body;
				const combined = Array.isArray(body)
					? body.filter(m => m.sections?.length > 0)
					: [
						...(body?.modules || []).filter(m => m.sections?.length > 0),
						...(body?.checklists || []).filter(m => m.sections?.length > 0),
					];
				setTempModules(combined);
			}
		});
	}, []);

	const toggleAccordion = (moduleId) =>
		setOpenAccordions(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));

	useEffect(() => {
		if (company['sections'] && company['sections'].length) {
			const extraModules = (company['extraModules'] && company['extraModules'].length) ? company['extraModules'].map((mod) => ({
				type: mod.type,
				_id: mod._id,
				sectionNo: mod.id, // or mod.moduleKey if you prefer that
				sectionName: mod.moduleName
			})) : [];

			const generateChecklistModules = company['generateChecklistModules'] && company['generateChecklistModules'].length ? company['generateChecklistModules'].map((mod) => {
				return {
					checklistModule: true,
					type: mod.type,
					_id: mod._id,
					sectionNo: mod.moduleKey,
					sectionName: mod.moduleName
				}
			}) : [];
			const _groupedSections = [...company['sections'], ...extraModules, ...generateChecklistModules].reduce((acc, section) => {
				let type = section.sectionNo.includes("UD0")
					? "Custom Section"
					: section.type && /extensions pack|extentions pack/i.test(section.type)
						? "Expension Pack"
						: section.type && section.type.includes("-")
							? section.type.split("-")[0].trim()
							: section.type;

				type = type?.trim();
				const allowedTypes = [
					"Existing Sections",
					"Expension Pack",
					"Additional Sections",
					"Custom Section"
				];

				if (!allowedTypes.includes(type)) {
					if (section?.checklistModule) {
						type = 'checklist'
					} else {
						type = "extraModules";
					}
				}

				if (!acc[type]) acc[type] = [];
				acc[type].push(section);
				return acc;
			}, {});
			setGroupedSections(_groupedSections)
		}
	}, [company['sections']])

	const permissionGroups = useMemo(() => {
		const groups = [];
		(tempModules || []).forEach(mod => {
			const sectionNos = (mod.sections || []).map(s => s.sectionNo).filter(Boolean);
			if (sectionNos.length) groups.push({ id: String(mod._id), name: mod.moduleName, key: mod.moduleKey, sectionNos });
		});
		if (groupedSections) {
			Object.entries(groupedSections)
				.filter(([type]) => !['Existing Sections', 'Expension Pack'].includes(type))
				.forEach(([, typeSections]) => {
					(typeSections || []).forEach(sec => {
						if (!sec.sectionNo) return;
						groups.push({ id: String(sec.sectionNo), name: sec.sectionName === 'Investor Lead Introduction' ? 'InvestorMatch' : sec.sectionName, key: sec.sectionNo, sectionNos: [sec.sectionNo] });
					});
				});
		}
		return groups;
	}, [tempModules, groupedSections]);

	const allSectionNos = useMemo(() => [...new Set(permissionGroups.flatMap(g => g.sectionNos))], [permissionGroups]);

	const memberHasSections = (member, sectionNos) => {
		if (!sectionNos?.length) return false;
		const perms = company?.['memberPermissions']?.[member['_id']] || {};
		return sectionNos.every(no => !!perms[no]);
	};

	const memberHasAllPermissions = (member) => memberHasSections(member, allSectionNos);

	const handleMemberEdit = (member) => {
		if (!setEditUser || !setUserAction) return;
		setEditUser({ ...member, permissions: company?.['memberPermissions']?.[member['_id']] });
		setUserAction('Edit');
		dispatch(toggleModal({ open: 'editUser' }));
	};

	const savePermissions = async (nextCompany, logData) => {
		const res = await postRequest(`/company/updatePermission/${companyId}`, { permissions: nextCompany['memberPermissions'], updateData: logData });
		if (res['code'] == 200) dispatch(setCompany(nextCompany));
		showMessage(res['message']);
		return res;
	};

	const applyMemberPermissions = async (member, nextMemberPerms, logData) => {
		const nextCompany = {
			...company,
			memberPermissions: { ...(company['memberPermissions'] || {}), [member['_id']]: nextMemberPerms }
		};
		return savePermissions(nextCompany, logData);
	};

	const setPermissions = async (e, sectionNo, member) => {
		if (!isAdmin) {
			e.preventDefault();
			return showMessage("Only admin can change permissions");
		}

		const action = e.target.checked ? "granted" : "revoked";
		const permissions = { ...(company?.['memberPermissions']?.[member['_id']] || {}), [sectionNo]: e.target.checked };
		await applyMemberPermissions(member, permissions, {
			message: user['firstName'] + " " + user['lastName'] + " " + action + " permission for " + sectionNo + " to " + member['firstName'] + " " + member['lastName'],
			userId: user['_id'],
			sectionNo,
			memberId: member['_id'],
			[member._id]: { [sectionNo]: e.target.checked }
		});
	};

	const setMemberSectionPermissions = async (member, sectionNos, grantAll, label) => {
		if (!isAdmin) return showMessage("Only admin can change permissions");
		if (!sectionNos?.length) return showMessage("No modules available to assign.");
		if (savingMemberId) return;

		setSavingMemberId(member['_id']);
		try {
			const current = { ...(company?.['memberPermissions']?.[member['_id']] || {}) };
			sectionNos.forEach(no => { current[no] = grantAll; });
			const action = grantAll ? "granted" : "revoked";
			await applyMemberPermissions(member, current, {
				message: `${user['firstName']} ${user['lastName']} ${action} ${label} for ${member['firstName']} ${member['lastName']}`,
				userId: user['_id'],
				memberId: member['_id'],
				[member._id]: current
			});
		} finally {
			setSavingMemberId(null);
		}
	};

	const handleBulkPermissions = (e, member, sectionNos, label) => {
		e.stopPropagation();
		setMemberSectionPermissions(member, sectionNos, e.target.checked, label);
	};

	const getSectionTitle = (type) => {
		if (type === "existing sections") {
			return "Core Modules";
		} else if (type === "expension pack") {
			return "Expansion Pack"
		} else if (type === "extentions pack" || type === "custom section" || type === "additional sections") {
			return capitalizeFirstLetter(type);
		} else if (type === "checklist") {
			return "Investor Checklist Modules"
		} else {
			return "Extra Modules";
		}
	};

	const getAuditLogs = async () => {
		const res = await postRequest(`/log`, { endpoint: `/api/company/updatePermission/${company['_id']}`, message: "Permissions updated" });
		setAllLogs(res['body']);
		if (res["code"] == 200) {
			dispatch(toggleModal({ open: 'teamPermissionLogs' }))
		}
	};

	const renderPermissionDropdown = (member, { readOnly = false } = {}) => {
		const isSaving = savingMemberId === member['_id'];
		const canEdit = isAdmin && !readOnly && !isSaving && allSectionNos.length > 0;
		return (
			<div className="pr" onClick={e => e.stopPropagation()}>
				<Select
					multiple
					className="select-advisor w10"
					id={`member-perm-select-${member['_id']}`}
					sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }}
					IconComponent={ExpandMoreIcon}
					value={[]}
					onChange={() => { }}
					disabled={isSaving || !allSectionNos.length}
					MenuProps={{
						PaperProps: { style: { maxHeight: 360, width: 'auto', minWidth: '240px' } },
						anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
						transformOrigin: { vertical: 'top', horizontal: 'right' },
					}}
				>
					<MenuItem value="all" className="select-advisor-list ttc" dense onClick={e => e.stopPropagation()}>
						<Checkbox
							checked={readOnly || memberHasAllPermissions(member)}
							disabled={!canEdit}
							onChange={(e) => handleBulkPermissions(e, member, allSectionNos, 'all permissions')}
							onClick={e => e.stopPropagation()}
						/>
						<ListItemText primary="All permissions" />
					</MenuItem>
					{permissionGroups.map(group => (
						<MenuItem key={group.id} value={group.id} className="select-advisor-list ttc" dense onClick={e => e.stopPropagation()}>
							<Checkbox
								checked={readOnly || memberHasSections(member, group.sectionNos)}
								disabled={!canEdit}
								onChange={(e) => handleBulkPermissions(e, member, group.sectionNos, group.name)}
								onClick={e => e.stopPropagation()}
							/>
							<ListItemText primary={group.key ? `${group.name} (${group.key})` : group.name} />
						</MenuItem>
					))}
				</Select>
				{isSaving && <span className="db fs10px c28 mt0-25rem">Saving…</span>}
			</div>
		);
	};

	return (
		<>
			<div className="df jcsb aic mr2rem ml1rem fww" style={{ gap: '1rem' }}>
				<div className="df fdc">
				</div>
				<div className="df bg1 br50px bw2px bss pr1rem p0-5rem bsbb aic cp add-btn-dashboard" onClick={getAuditLogs}>
					<span className='bg1 bsbb br100 w40px h40px tac df jcc aic'>
						<AuditLogIcon />
					</span>
					<span className='c15'>Audit Logs</span>
				</div>
			</div>
			<div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr mb2rem">
				<div className="table-permission">
					<table id="table" className="table-userPermissions table-clientPermissions 96">
						<thead className="ps t0">
							<tr>
								<th className="index-table">Modules</th>
								{company['creatorId'] &&
									<th className='user-area'>
										<div className="df aic jcsb">
											<div className="df aic">
												<div className='mr1rem'>
													{company['creatorId']?.['profile']
														? <img data-tooltip={company['creatorId']?.firstName + " " + (company['creatorId']?.lastName && company['creatorId']?.lastName[0] ? company['creatorId']?.lastName[0] : "")} src={company['creatorId']?.['profile']} className={`shadow w2rem h2rem df jcc aic c0 br100 bss bow1px bo31 `} />
														: <span
															data-tooltip={company['creatorId']?.firstName + " " + (company['creatorId']?.lastName && company['creatorId']?.lastName[0] ? company['creatorId']?.lastName[0] : "")}
															className={`shadow w2rem h2rem df jcc aic c0 br100 bss bow1px bo31`}
														>
															{company['creatorId']?.firstName ? (company['creatorId']?.firstName[0] + (company['creatorId']?.lastName ? company['creatorId']?.lastName[0] : "")) : "?"}
														</span>
													}
												</div>
												<div className="df fdc">
													<span className="fs0-75rem">{company['creatorId']['firstName']} {company['creatorId']['lastName']} <span className="c4">(Admin)</span></span>
													<p className='fs0-75rem'>Status: <span className={`${company['creatorId']['emailVerified'] ? 'c7' : 'c13'}`}>{company['creatorId']['emailVerified'] ? 'Activated' : 'Inactive'}</span></p>
													{company['creatorId']?.['lastLogin'] && <p className='fs0-75rem'>Last Login: <span className={((new Date().getTime() - new Date(company['creatorId']['lastLogin']).getTime()) / (1000 * 3600 * 24 * 30)) > 3 ? 'c9' : 'c7'}>{moment(company['creatorId']['lastLogin']).format('ll')}</span></p>}
												</div>
											</div>
											{renderPermissionDropdown(company['creatorId'], { readOnly: true })}
										</div>
									</th>
								}
								{teamMembers.map(member => {
									return (
										<th className='user-area' key={member._id}>
											<div className="df aic jcsb">
												<div className="df aic cp" onClick={() => handleMemberEdit(member)}>
													<div className='mr1rem'>
														{member?.profile
															? <img data-tooltip={member?.firstName + " " + (member?.lastName && member?.lastName[0] ? member?.lastName[0] : '')} src={member?.profile} className="shadow w2rem h2rem df jcc aic c0 br100 bss bow1px bo31" />
															: <span data-tooltip={member?.firstName + " " + (member?.lastName && member?.lastName[0] ? member?.lastName[0] : '')} className="shadow w2rem h2rem df jcc aic c0 br100 bss bow1px bo31">{member?.firstName ? (member?.firstName[0] + (member?.lastName ? member?.lastName[0] : "")) : "?"}</span>
														}
													</div>
													<div className="df fdc">
														<span className="fs0-75rem">{member['firstName']} {member['lastName']}</span>
														{member['designation'] && <span className="fs0-75rem c28 w100">{member['designation']}</span>}
														<p className='fs0-75rem'>Status: <span className={`${member['emailVerified'] ? 'c7' : 'c8'}`}>{member['emailVerified'] ? 'Activated' : `Invite Sent - (${moment(member['updatedAt']).format('ll')})`}</span></p>
														{member['emailVerified'] && member['lastLogin'] && <p className='fs0-75rem'>Last Login: <span className={((new Date().getTime() - new Date(member['lastLogin']).getTime()) / (1000 * 3600 * 24 * 30)) > 3 ? 'c9' : 'c7'}>{moment(member['lastLogin']).format('ll')}</span></p>}
													</div>
												</div>
												{renderPermissionDropdown(member)}
											</div>
										</th>
									)
								})}
							</tr>
						</thead>
						<tbody>
							{/* ── Temp Modules accordion ─────────────────────────────────────── */}
							{tempModules.length > 0 && (
								<>
									{tempModules.map((mod, mIdx) => (
										<React.Fragment key={mod._id}>
											{/* Module accordion header row */}
											<tr
												onClick={() => toggleAccordion(mod._id)}
												style={{ background: '#f8f9fa', cursor: 'pointer', borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none' }}
											>
												<td className="team-temp-module-head-cell" style={{ padding: '10px 12px' }}>
													<div className="df aic jcsb w100">
														<div className="df aic" style={{ gap: '8px' }}>
															<ExpandMoreIcon className="c3 td250" style={{ fontSize: '22px', transform: openAccordions[mod._id] ? 'rotate(180deg)' : 'rotate(0deg)' }} />
															<span className="fw600 c15 fs1rem">{mod.moduleName}</span>
															{mod.moduleKey && (
																<span className="fs0-75rem c15 px0-5rem py0-25rem br5px" style={{ background: '#e9ecef', opacity: 0.8 }}>
																	{mod.moduleKey}
																</span>
															)}
															<span className="fs0-75rem" style={{ color: '#888' }}>
																({mod.sections?.length || 0} section{mod.sections?.length !== 1 ? 's' : ''})
															</span>
														</div>
													</div>
												</td>
												{company['creatorId'] && (
													<td style={{ background: '#f8f9fa' }} onClick={e => e.stopPropagation()}>
														<label className="df jcc fs0-75rem">
															<span className="checkbox w20px h20px pr">
																<input disabled defaultChecked className="shadow bw2px bss w20px h20px br5px" type="checkbox" style={{ backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' }} />
															</span>
														</label>
													</td>
												)}
												{teamMembers.map((member) => {
													const moduleSectionNos = (mod.sections || []).map(s => s.sectionNo).filter(Boolean);
													return (
														<td key={member._id} style={{ background: '#f8f9fa' }} onClick={e => e.stopPropagation()}>
															<label className="df jcc cp fs0-75rem">
																<span className="cp checkbox w20px h20px pr">
																	<input
																		type="checkbox"
																		checked={memberHasSections(member, moduleSectionNos)}
																		disabled={!isAdmin || savingMemberId === member['_id']}
																		onChange={(e) => handleBulkPermissions(e, member, moduleSectionNos, mod.moduleName)}
																		className="bg5 shadow bo5 bw2px bss cp td250 w20px h20px br5px"
																		style={!isAdmin ? { backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' } : {}}
																	/>
																</span>
															</label>
														</td>
													);
												})}
											</tr>

											{/* Section rows */}
											{openAccordions[mod._id] && (mod.sections || []).map((sec, sIdx) => (
												<tr key={sec.sectionNo}>
													<td className="c15 fs12px">
														<span className="fw600 fs12px" style={{ marginRight: '4px' }}>{sec.sectionNo}</span>
														{sec.sectionName}
													</td>
													{company['creatorId'] && (
														<td className="c6 cp">
															<label className="df jcc cp fs0-75rem">
																<span className="checkbox w20px h20px pr">
																	<input
																		disabled
																		defaultChecked
																		id={`temp-owner-${mod._id}-${sIdx}`}
																		className="shadow bw2px bss cp td250 w20px h20px br5px"
																		type="checkbox"
																		style={{ backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' }}
																	/>
																</span>
															</label>
														</td>
													)}
													{teamMembers.map((member) => (
														<td className="c6 cp" key={member._id}>
															<label className="df jcc cp fs0-75rem">
																<span className="cp checkbox w20px h20px pr">
																	<input
																		checked={!!company?.['memberPermissions']?.[member['_id']]?.[sec.sectionNo]}
																		onChange={(e) => setPermissions(e, sec.sectionNo, member)}
																		id={`${sec.sectionNo}-${member["_id"]}`}
																		className="bg5 shadow bo5 bw2px bss cp td250 w20px h20px br5px"
																		type="checkbox"
																		style={!isAdmin ? { backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' } : {}}
																	/>
																</span>
															</label>
														</td>
													))}
												</tr>
											))}
										</React.Fragment>
									))}
								</>
							)}

							{
								groupedSections && Object.entries(groupedSections)
									.filter(([type]) => !['Existing Sections', 'Expension Pack'].includes(type))
									.map(([type, typeSections], inde) => {
										return (
											<>
												<tr key={`type-heading-${type}`}>
													<td style={{ paddingTop: "40px" }} colSpan={teamMembers.length + 2} className="type-heading">
														<h3 style={{ borderTop: `${inde > 0 || tempModules.length > 0 ? "1px solid rgb(219, 223, 238)" : "transparent"}`, paddingTop: "12px" }} className="c15">
															{getSectionTitle(type.toLowerCase())}
														</h3>
													</td>
												</tr>
												{typeSections.map((section, idx2) => {
													return (
														<tr key={`company['sections']-section-${idx2}`}>
															{/* Map Sections */}
															<td className='c15 fs12px'>
																<span className='fw600 fs12px'>{section['sectionNo']}</span> {section['sectionName'] === "Investor Lead Introduction" ? "InvestorMatch" : section['sectionName']}
															</td>
															{/* Map Owner Permission */}
															<td className='c6 cp' key={idx2}>
																<label for={`owner-permission-${idx2}`} className="df jcc cp fs0-75rem">
																	<span className='checkbox w20px h20px pr'>
																		<input disabled={true} defaultChecked={true} id={`permission-${idx2}`} className="shadow bw2px bss cp td250 w20px h20px br5px" type="checkbox" style={{ backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' }} />
																	</span>
																</label>
															</td>
															{/* Map Members Permission of that section */}
															{teamMembers.map((member) => (
																<td className='c6 cp' key={member._id}>
																	<label className="df jcc cp fs0-75rem">
																		<span className='cp checkbox w20px h20px pr'>
																			<input
																				checked={!!company?.['memberPermissions']?.[member['_id']]?.[section['sectionNo']]}
																				onChange={(e) => setPermissions(e, section['sectionNo'], member)}
																				id={`${section['sectionNo']}-${member['_id']}`}
																				className="bg5 shadow bo5 bw2px bss cp td250 w20px h20px br5px"
																				type="checkbox"
																				style={!isAdmin ? { backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' } : {}}
																			/>
																		</span>
																	</label>
																</td>
															))}
														</tr>
													)
												})}
											</>
										)
									})}

							{/* Core Modules & Expansion Pack permissions — hidden for now
							{
								groupedSections && Object.entries(groupedSections)
									.filter(([type]) => ['Existing Sections', 'Expension Pack'].includes(type))
									.map(([type, typeSections], inde) => {
									return (
										<>
											<tr key={`type-heading-${type}`}>
												<td style={{ paddingTop: "40px" }} colSpan={(company?.['members'] ?? []).length + 1} className="type-heading">
													<h3 style={{ borderTop: `${inde > 0 ? "1px solid rgb(219, 223, 238)" : "transparent"}`, paddingTop: "12px" }} className="c15">
														{getSectionTitle(type.toLowerCase())}
													</h3>
												</td>
											</tr>
											{typeSections.map((section, idx2) => {
												return (
													<tr key={`company['sections']-section-${idx2}`}>
														<td className='c15'>
															<span className='fw600'>{section['sectionNo']}</span> {section['sectionName'] === "Investor Lead Introduction" ? "InvestorMatch" : section['sectionName']}
														</td>
														<td className='c6 cp' key={idx2}>
															<label for={`owner-permission-${idx2}`} className="df jcc cp fs0-75rem">
																<span className='checkbox w20px h20px pr'>
																	<input disabled={true} defaultChecked={true} id={`permission-${idx2}`} className="shadow bw2px bss cp td250 w20px h20px br5px" type="checkbox" style={{ backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' }} />
																</span>
															</label>
														</td>
														{company['members'] && company['members'].filter((mem) => mem._id !== company?.['creatorId']?.['_id']).map((member, idx) => {
															return (
																<td className='c6 cp' key={idx}>
																	<label for={`member-permission-${idx}`} className="df jcc cp fs0-75rem">
																		<span className='cp checkbox w20px h20px pr'>
																			<input
																				checked={company?.['memberPermissions']?.[member['_id']]?.[section['sectionNo']]}
																				onChange={(e) => setPermissions(e, section['sectionNo'], member)}
																				id={`${section['sectionNo']}-${member['_id']}`}
																				className="bg5 shadow bo5 bw2px bss cp td250 w20px h20px br5px"
																				type="checkbox"
																				style={
																					user['_id'] !== company['creatorId']['_id']
																						? { backgroundColor: '#8f8f8f', borderColor: '#8f8f8f' }
																						: {}
																				}
																			/>
																		</span>
																	</label>
																</td>
															)
														})}
													</tr>
												)
											})}
										</>
									)
								})}
							*/}
						</tbody>
					</table>
				</div>
			</div>


			{/* Audit Log */}
			<TeamPermissionLogs
				getViewableURL={getViewableURL}
				profileClass={"profileClass"}
				modalType={"teamPermissionLogs"}
				logList={allLogs}
			/>
		</>
	);
}
